/**
 * sRGB hex → OKLCH conversion.
 *
 * The design system speaks `oklch()` for every brand-derived token, so the
 * Settings page lets users pick a hex color and we convert at apply time.
 *
 * Formulas are the public ones from Björn Ottosson's OKLab paper, simplified
 * for in-gamut sRGB inputs. Output is `oklch(L C H)` with L 0–1, C 0–~0.4,
 * H in degrees. CSS Color Module 4 ingests this directly.
 */

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Convert "#RRGGBB" or "#RGB" → `oklch(L C H)` string. Returns null on bad input. */
export function hexToOklch(hex: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = srgbToLinear(parseInt(h.slice(0, 2), 16) / 255);
  const g = srgbToLinear(parseInt(h.slice(2, 4), 16) / 255);
  const b = srgbToLinear(parseInt(h.slice(4, 6), 16) / 255);

  // Linear sRGB → LMS
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m_ = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m__ = Math.cbrt(m_);
  const s_ = Math.cbrt(s);

  // LMS → OKLab
  const L = 0.2104542553 * l_ + 0.793617785 * m__ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m__ + 0.4505937099 * s_;
  const bb = 0.0259040371 * l_ + 0.7827717662 * m__ - 0.808675766 * s_;

  // OKLab → OKLCH
  const C = Math.hypot(a, bb);
  let H = (Math.atan2(bb, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`;
}
